import React from "react";
import "./index.less";
import { ITableProps } from "./interface";
import ZIcon from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
import Table from "antd/lib/table";
import {
	SortableContainer,
	SortableElement,
	SortableHandle
} from "react-sortable-hoc";
import useMobileView from "@/Hooks/useMobileView";
import ZContentLoadingIcon from "@atoms/ZContentLoadingIcon";
// import useMobileView from "@/Hooks/useMobileView";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ZTable = <T extends Record<string, any>>({
	dataSource,
	columns,
	isDraggable = false,
	isTopBorder = true,
	headerBgColor = false,
	className = "",
	onDragEnd,
	loading,
	loadingIcon,
	useBlueHeader = false,
	draggableRowInDrawers = false,
	...rest
}: ITableProps<T>): React.ReactElement => {
	const isMobileView = useMobileView();
	columns = columns.map(column =>
		column.sorter
			? {
					...column,
					fixed: isMobileView ? false : column.fixed,
					title: ({ sortColumns }) => {
						const sortColumn = sortColumns?.find(
							sortColumn => sortColumn.column.key === column.key
						);
						if (
							sortColumn &&
							sortColumn.column.key === column.key
						) {
							switch (sortColumn.order) {
								case "ascend":
									return (
										<div className="flex items-center">
											{column.title}
											<ZIcon
												name={EIcons.SORTUP}
												size="sm"
												className="ml-2"
											/>
										</div>
									);
								case "descend":
									return (
										<div className="flex items-center">
											{column.title}
											<ZIcon
												name={EIcons.SORTDOWN}
												size="sm"
												className="ml-2"
											/>
										</div>
									);
								default:
									return column.title;
							}
						} else return column.title;
					}
			  }
			: { ...column, fixed: isMobileView ? false : column.fixed }
	);

	if (isDraggable) {
		const DragHandle = SortableHandle(
			({ isDisabled }: { isDisabled?: boolean }) => (
				<ZIcon
					className={`${
						rest?.isDragDisabled || isDisabled
							? "pointer-events-none"
							: "icon-grab"
					}`}
					name={EIcons.DRAG}
					colorVariant={rest?.isDragDisabled || isDisabled ? 5 : 7}
				/>
			)
		);
		columns.unshift({
			title: "",
			dataIndex: "sort",
			width: 50,
			fixed: "left",
			className: "drag-visible-z-table-row",
			render: (_text: unknown, record: any) => {
				const canReorder = record.canReorder !== false;
				return canReorder ? <DragHandle isDisabled={false} /> : null;
			}
		});
		const SortableItem = SortableElement(({ ...props }) => (
			<tr
				{...props}
				className={
					(props.className,
					draggableRowInDrawers ? "draggable-row" : "")
				}
			/>
		));
		const SortableContainerItem = SortableContainer(({ ...props }) => (
			<tbody {...props} />
		));
		const DraggableBodyRow = ({ ...restProps }) => {
			const index =
				dataSource &&
				dataSource.findIndex(
					x => x.index === restProps["data-row-key"]
				);
			const rowData = dataSource && dataSource[index >= 0 ? index : 0];
			const canReorder = rowData?.canReorder !== false;
			const sortableIndex = index >= 0 ? index : 0;

			return (
				<SortableItem
					index={sortableIndex}
					{...restProps}
					disabled={!canReorder}
				/>
			);
		};

		const DraggableContainer = ({ ...dragprops }) => (
			<SortableContainerItem
				useDragHandle
				disableAutoscroll
				helperClass="draggable-z-table-row"
				onSortEnd={onDragEnd}
				{...dragprops}
			/>
		);

		return (
			<Table<T>
				dataSource={dataSource}
				columns={columns}
				rowKey="index"
				components={{
					body: {
						wrapper: DraggableContainer,
						row: DraggableBodyRow
					}
				}}
				{...rest}
				className={`z-table ${
					isTopBorder ? "add-border-top" : "no-top-border"
				} ${className}`}
			/>
		);
	}

	return (
		<Table<T>
			dataSource={dataSource}
			columns={columns}
			{...rest}
			loading={
				loading
					? {
							indicator: (
								<ZContentLoadingIcon
									loading_icon={loadingIcon}
								/>
							),
							spinning: loading
					  }
					: undefined
			}
			className={`z-table 
			${headerBgColor ? "add-bg-header" : "no-bg-header"} 
			${isTopBorder ? "add-border-top" : ""} 
			${useBlueHeader ? "use-blue-header" : ""} ${className}`}
		/>
	);
};

export default ZTable;
